import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Icon from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ChevronRight";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LineArrowRight from "@sunSource/Icons/LineArrowRight";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import React, { useRef, useState, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import sortBy from "lodash/sortBy";
import PageProps from "@insite/client-framework/Types/PageProps";
import { NewsPageModel } from "@sunSource/Store/Data/TabsData/TabsDataState";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    defaultPageSize = "defaultPageSize",
    recentArticlesTitle = "recentArticlesTitle",
    recentMediaTitle = "recentMediaTitle",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.recentArticlesTitle]: string;
        [fields.recentMediaTitle]: string;
    };
}

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        pages: pages.value || [],
        activeTabData: state.sunSourceState.TabsActiveData.listActiveTabData,
        tabsSearchTerm: state.sunSourceState.TabsActiveData.tabsSearchTerm,
        activeTabName: state.sunSourceState.TabsActiveData.activeTab,
        links,
    };
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps>;

export interface RecentMediaArticlesStyles {
    title: TypographyPresentationProps;
    linkText: LinkPresentationProps;
    linkWrapper?: InjectableCss;
}

export const recentMediaArticlesStyles: RecentMediaArticlesStyles = {
    title: {
        css: css`
            color: ${getColor("primary")};
            text-transform: uppercase;
        `,
    },
    linkText: {
        css: css`
            > span {
                color: #545353 !important;
                font-size: 15px;
                display: flex;
                align-items: center;
                gap: 5px;
                > span {
                    width: 16px;
                    height: 16px;
                    position: relative;
                    top: 0.5px;
                    > svg {
                        width: 16px;
                        height: 16px;
                    }
                }
            }
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
        `,
    },
};

const styles = recentMediaArticlesStyles;

const RecentMediaArticles: React.FC<Props> = props => {
    const { pages, activeTabData, tabsSearchTerm, fields, activeTabName, links } = props;
    const { defaultPageSize, recentArticlesTitle, recentMediaTitle } = fields;
    const sortedAndPagedPages = sortBy(pages, o => o.fields["publishDate"]).reverse();
    const isTabOrSearchActive = (activeTabData && activeTabData.length > 0) || !!tabsSearchTerm;
    const currentActiveTabData = isTabOrSearchActive ? activeTabData : sortedAndPagedPages;
    const extractAttachmentList =
        activeTabName === "" &&
        currentActiveTabData
            ?.filter(
                (list: PageProps | NewsPageModel) =>
                    list.generalFields["attachments"] && list.generalFields["attachments"].length > 0,
            )
            .slice(0, defaultPageSize);
    const extractArticlesList = currentActiveTabData?.slice(0, defaultPageSize);

    return (
        <StyledWrapper>
            <Typography {...styles.title} variant="h2">
                {activeTabName === "" ? recentMediaTitle : recentArticlesTitle}
            </Typography>
            <StyledWrapper {...styles.linkWrapper}>
                {extractAttachmentList && extractAttachmentList.length > 0
                    ? extractAttachmentList.map((item: PageProps | NewsPageModel) => {
                         if(item?.fields?.hideFromUi === true || item?.fields?.hideFromUi === "true"){
                            return null;
                        }
                          const attachments = item.generalFields["attachments"] as {
                              fields: { name: string; file: string };
                          }[];
                          return attachments.map(attachment => (
                              <StyledWrapper key={attachment.fields.name}>
                                  <Link
                                      {...styles.linkText}
                                      href={links[item.nodeId]?.url}
                                      data-test-selector="newslist_recentMediaArticles"
                                  >
                                      {attachment?.fields?.name || item.fields["title"]}
                                      <Icon src={LineArrowRight} />
                                  </Link>
                              </StyledWrapper>
                          ));
                      })
                    : extractArticlesList?.map((item: PageProps | NewsPageModel) => {
                        if(item?.fields?.hideFromUi === true || item?.fields?.hideFromUi === "true"){
                            return null;
                        }
                          return (
                              <StyledWrapper key={item.fields.title}>
                                  <Link
                                      {...styles.linkText}
                                      href={links[item.nodeId]?.url}
                                      data-test-selector="newslist_recentMediaArticles"
                                  >
                                      {item.fields["title"]}
                                      <Icon src={LineArrowRight} />
                                  </Link>
                              </StyledWrapper>
                          );
                      })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RecentMediaArticles),
    definition: {
        displayName: "Recent Media Articles",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
                sortOrder: 1,
            },
            {
                name: fields.recentArticlesTitle,
                defaultValue: "Recent Articles",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.recentMediaTitle,
                defaultValue: "Recent Media",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
